import { AppAuthContextLayout } from "@dust-tt/front/components/sparkle/AppAuthContextLayout";
import { computeIsMetronomeCheckout } from "@dust-tt/front/lib/client/subscription";
import { shouldEnterOnboarding } from "@dust-tt/front/lib/onboarding";
import { useUserMetadata } from "@dust-tt/front/lib/swr/user";
import { useAuthContext } from "@dust-tt/front/lib/swr/workspaces";
import { ONBOARDING_STEP_METADATA_KEY } from "@dust-tt/front/types/onboarding";
import { Spinner } from "@dust-tt/sparkle";
import { AuthErrorPage } from "@spa/app/components/AuthErrorPage";
import { useAppReadyContext } from "@spa/app/contexts/AppReadyContext";
import { useRequiredPathParam } from "@spa/lib/platform";
import { type ReactNode, useEffect } from "react";
import { Navigate, Outlet, useMatches } from "react-router-dom";

function useIsRequireCanUseProduct(): boolean {
  const matches = useMatches();
  return matches.every(
    (match) =>
      (match.handle as { requireCanUseProduct?: boolean } | undefined)
        ?.requireCanUseProduct !== false
  );
}

function useIsRequireOnboardingComplete(): boolean {
  const matches = useMatches();
  return matches.every(
    (match) =>
      (match.handle as { requireOnboardingComplete?: boolean } | undefined)
        ?.requireOnboardingComplete !== false
  );
}

interface WorkspacePageProps {
  children?: ReactNode;
}

export function WorkspacePage({ children }: WorkspacePageProps) {
  const wId = useRequiredPathParam("wId");
  const isRequireCanUseProduct = useIsRequireCanUseProduct();
  const isRequireOnboardingComplete = useIsRequireOnboardingComplete();
  const { metadata: onboardingStepMeta, isMetadataLoading } = useUserMetadata(
    ONBOARDING_STEP_METADATA_KEY
  );

  const { authContext, isAuthenticated, authContextError } = useAuthContext({
    workspaceId: wId,
  });

  const signalAppReady = useAppReadyContext();

  // Signal that the app is ready when auth is loaded or on error
  // This will dismiss the loading screen
  useEffect(() => {
    if ((isAuthenticated && authContext) || authContextError) {
      signalAppReady();
    }
  }, [isAuthenticated, authContext, authContextError, signalAppReady]);

  if (authContextError) {
    return <AuthErrorPage error={authContextError} />;
  }

  // Return null while loading - the loading screen handles the loading state
  if (!isAuthenticated || !authContext) {
    return null;
  }

  const canUseProduct = authContext.subscription.plan.limits.canUseProduct;

  // Not using `useIsMetronomeCheckout` here: the hook reads feature flags from
  // the auth context provider, which this component is about to mount.
  const isMetronomeCheckout = computeIsMetronomeCheckout({
    featureFlags: authContext.featureFlags,
  });

  // Paywall enforcement: redirect when canUseProduct is false
  // and the current route requires canUseProduct (via route handle).
  // Eligible workspaces on the credit-priced checkout skip plan selection and
  // activate the free plan directly, since every new workspace gets free credits.
  if (!canUseProduct && isRequireCanUseProduct) {
    const target = authContext.isEligibleForTrial
      ? isMetronomeCheckout
        ? "activate"
        : "trial"
      : "subscribe";
    return <Navigate to={`/w/${wId}/${target}`} replace />;
  }

  if (isRequireCanUseProduct && isRequireOnboardingComplete) {
    if (isMetadataLoading) {
      return (
        <AppAuthContextLayout authContext={authContext}>
          <div className="flex h-screen items-center justify-center">
            <Spinner />
          </div>
        </AppAuthContextLayout>
      );
    }
    if (shouldEnterOnboarding(onboardingStepMeta?.value ?? null)) {
      return <Navigate to={`/w/${wId}/onboarding`} replace />;
    }
  }

  return (
    <AppAuthContextLayout authContext={authContext}>
      {children ?? <Outlet />}
    </AppAuthContextLayout>
  );
}
